import { ofetch } from 'ofetch'

/**
 * Load a script programmatically
 */
export function loadScript(source: string) {
  return new Promise(function (resolve, reject) {
    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${source}"]`,
    )
    if (!script) {
      script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = source
      document.head.append(script)
    } else if (script.getAttribute('loaded')) {
      resolve(script)
      return
    }

    script.addEventListener('error', reject)
    script.addEventListener('abort', reject)
    script.addEventListener('load', () => {
      script?.setAttribute('data-loaded', 'true')
      resolve(script)
    })
  })
}

/**
 * A simple promise that resolves after a given time
 * @example
 * async function test() {
 *   console.log('before wait')
 *   await wait(1000)
 *   console.log('waited 1 second')
 * }
 */
export function wait(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export interface FetchHTMLOptions<T> {
  /** The function to extract the HTML string from the response */
  extract: (data: T) => string
  /** The element to replace the content with or a function */
  replace?: string | Element | ((html: string) => void)
  /**
   * If the UI shows a loading state too briefly, the user could think there was a bug
   * @param delay - The delay in milliseconds
   * @default 300
   */
  delay?: number
}

/**
 * Fetch HTML from a URL and replace the content of a DOM element
 * @param url - The URL to fetch
 * @param options - The options for the fetch
 * @returns The response
 */
export async function fetchHTML<T = { data: string }>(
  url: string,
  options: FetchHTMLOptions<T>,
) {
  const [response] = await Promise.all([
    ofetch<T>(url),
    wait(options.delay ?? 300),
  ])

  const html = options.extract(response)

  if (options.replace) {
    if (typeof options.replace === 'string') {
      const parent = document.querySelector(options.replace)
      if (parent) {
        parent.innerHTML = html
      }
    } else if (options.replace instanceof Element) {
      options.replace.innerHTML = html
    } else if (typeof options.replace === 'function') {
      options.replace(html)
    }
  }

  return response
}

export default { loadScript, wait, fetchHTML }
